import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Zap,
  Target,
  Gauge,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface ProductionMetrics {
  costReduction: number;
  successRate: number;
  averageProcessingTime: number;
  utilityCompaniesSupported: number;
  dataAccuracy: number;
  mimeTypeErrors: number;
  totalCostSavings: number;
  performanceScore: number;
}

interface OptimizationRecommendation {
  id: string;
  type: 'cost' | 'performance' | 'accuracy' | 'error';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  estimatedImpact: string;
  status: 'pending' | 'implementing' | 'completed';
}

export function ProductionOptimizationDashboard() {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState('7d');
  const [isOptimizing, setIsOptimizing] = useState(false);

  // Fetch production metrics
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery({
    queryKey: ['production-metrics', timeRange],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('production-optimization', {
        body: { action: 'getMetrics', timeRange }
      });
      if (error) throw error;
      return data.metrics as ProductionMetrics;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  // Fetch optimization recommendations
  const { data: recommendations, isLoading: recommendationsLoading } = useQuery({
    queryKey: ['optimization-recommendations'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('production-optimization', {
        body: { action: 'getRecommendations' }
      });
      if (error) throw error;
      return data.recommendations as OptimizationRecommendation[];
    },
    enabled: !!user,
  });

  // Fetch trend data
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['production-trends', timeRange],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('production-optimization', {
        body: { action: 'getTrends', timeRange }
      });
      if (error) throw error;
      return data.trends;
    },
    enabled: !!user,
  });

  const handleOptimizeAll = async () => {
    setIsOptimizing(true);
    try {
      await supabase.functions.invoke('production-optimization', {
        body: { action: 'optimizeAll' }
      });
      await refetchMetrics();
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsOptimizing(false);
    }
  };

  const getMetricStatus = (value: number, target: number, reverse = false) => {
    const percentage = reverse ? ((target - value) / target) * 100 : (value / target) * 100;
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'error';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (metricsLoading || recommendationsLoading || trendLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Production Optimization</h2>
          <div className="animate-pulse bg-gray-200 h-10 w-32 rounded"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 h-32 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Production Optimization</h2>
          <p className="text-muted-foreground">Monitor and optimize production performance</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleOptimizeAll} 
            disabled={isOptimizing}
            className="flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {isOptimizing ? 'Optimizing...' : 'Optimize All'}
          </Button>
          <Button variant="outline" onClick={() => refetchMetrics()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Success Criteria Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MIME Type Errors</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.mimeTypeErrors || 0}</div>
            <p className="text-xs text-muted-foreground">
              Target: 0 errors
            </p>
            <div className={`text-sm ${getStatusColor(getMetricStatus(metrics?.mimeTypeErrors || 0, 0, true))}`}>
              {metrics?.mimeTypeErrors === 0 ? '✓ Target achieved' : '⚠ Needs attention'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Reduction</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.costReduction || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Target: 90%
            </p>
            <Progress value={metrics?.costReduction || 0} className="mt-2" />
            <div className={`text-sm mt-1 ${getStatusColor(getMetricStatus(metrics?.costReduction || 0, 90))}`}>
              ${metrics?.totalCostSavings || 0} saved
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.successRate || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Target: 99%+
            </p>
            <Progress value={metrics?.successRate || 0} className="mt-2" />
            <div className={`text-sm mt-1 ${getStatusColor(getMetricStatus(metrics?.successRate || 0, 99))}`}>
              {metrics?.successRate >= 99 ? '✓ Target achieved' : '↗ Improving'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.averageProcessingTime || 0}s</div>
            <p className="text-xs text-muted-foreground">
              Target: &lt;15s
            </p>
            <Progress 
              value={Math.max(0, 100 - ((metrics?.averageProcessingTime || 0) / 15) * 100)} 
              className="mt-2" 
            />
            <div className={`text-sm mt-1 ${getStatusColor(getMetricStatus(metrics?.averageProcessingTime || 0, 15, true))}`}>
              {(metrics?.averageProcessingTime || 0) < 15 ? '✓ Target achieved' : '⚠ Optimization needed'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utility Companies</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.utilityCompaniesSupported || 0}</div>
            <p className="text-xs text-muted-foreground">
              Target: 50+
            </p>
            <Progress value={((metrics?.utilityCompaniesSupported || 0) / 50) * 100} className="mt-2" />
            <div className={`text-sm mt-1 ${getStatusColor(getMetricStatus(metrics?.utilityCompaniesSupported || 0, 50))}`}>
              {(metrics?.utilityCompaniesSupported || 0) >= 50 ? '✓ Target achieved' : '↗ Expanding'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Accuracy</CardTitle>
            <Gauge className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.dataAccuracy || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Target: 95%+
            </p>
            <Progress value={metrics?.dataAccuracy || 0} className="mt-2" />
            <div className={`text-sm mt-1 ${getStatusColor(getMetricStatus(metrics?.dataAccuracy || 0, 95))}`}>
              {(metrics?.dataAccuracy || 0) >= 95 ? '✓ Target achieved' : '↗ Optimizing'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trends" className="space-y-6">
        <TabsList>
          <TabsTrigger value="trends">Performance Trends</TabsTrigger>
          <TabsTrigger value="recommendations">Optimization</TabsTrigger>
          <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Trends</CardTitle>
              <CardDescription>Track key metrics over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="successRate" stroke="#10b981" name="Success Rate %" />
                  <Line type="monotone" dataKey="processingTime" stroke="#f59e0b" name="Processing Time (s)" />
                  <Line type="monotone" dataKey="costEfficiency" stroke="#3b82f6" name="Cost Efficiency %" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-6">
          <div className="grid gap-4">
            {recommendations?.map((rec) => (
              <Card key={rec.id}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle className="text-base">{rec.title}</CardTitle>
                    <CardDescription>{rec.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={rec.priority === 'high' ? 'destructive' : rec.priority === 'medium' ? 'default' : 'secondary'}>
                      {rec.priority}
                    </Badge>
                    <Badge variant="outline">{rec.estimatedImpact}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status: {rec.status}</span>
                    <Button size="sm" variant="outline">
                      {rec.status === 'pending' ? 'Implement' : rec.status === 'implementing' ? 'View Progress' : 'Completed'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="costs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cost Optimization Analysis</CardTitle>
              <CardDescription>Monitor cost reduction and savings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>API Costs</span>
                    <span className="font-bold">${(trendData?.[0]?.apiCosts || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Processing Costs</span>
                    <span className="font-bold">${(trendData?.[0]?.processingCosts || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Total Savings</span>
                    <span className="font-bold text-green-600">${metrics?.totalCostSavings || 0}</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trendData?.slice(-7) || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="totalCosts" fill="#3b82f6" name="Daily Costs ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}